/**
 * Хелпер подгрузки lazy-чанков бандла (phone, map, video).
 *
 * Чанки собираются отдельными вызовами esbuild как самостоятельные iife-бандлы
 * и лежат рядом с главным: `plp-widgets-{name}-v{VERSION}.js`. Они НЕ являются
 * ES-модулями, поэтому импорт через `<script>` тег + Promise по `load`/`error`.
 *
 * Каждый чанк перед exit'ом обязан выставить флаг `window.__plpChunks[name] = true`,
 * чтобы повторный вызов `loadChunk` для уже загруженного чанка резолвился сразу.
 *
 * Singleton'ы из чанка сами регистрируются через `window.__plpRuntime[name] = ...`.
 * Потребитель (например `<plp-field type="phone">`) ждёт `loadChunk('phone')`,
 * затем читает `window.__plpRuntime.phone`.
 */

import { VERSION } from './version.js';

declare global {
  interface Window {
    __plpWidgets?: { version: string; loaded: true };
    __plpChunks?: Record<string, true>;
    __plpRuntime?: Record<string, unknown>;
    __plpConfig?: { widgetsCdn?: string };
  }
}

const inflight = new Map<string, Promise<void>>();

function chunkUrl(name: string): string {
  // CDN-домен может быть переопределён через глобал (см. шаг 03 про
  // window.__plpConfig). Иначе бандл и чанки лежат на одном origin.
  const cdn = (window.__plpConfig?.widgetsCdn ?? '').replace(/\/$/, '');
  return `${cdn}/widgets/plp-widgets-${name}-v${VERSION}.js`;
}

export function isChunkLoaded(name: string): boolean {
  return Boolean(window.__plpChunks?.[name]);
}

export function loadChunk(name: string): Promise<void> {
  if (isChunkLoaded(name)) {
    return Promise.resolve();
  }
  const existing = inflight.get(name);
  if (existing) {
    return existing;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = chunkUrl(name);
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      window.__plpChunks ??= {};
      window.__plpChunks[name] = true;
      resolve();
    };
    script.onerror = () => {
      inflight.delete(name);
      reject(new Error(`plp-widgets: failed to load chunk "${name}"`));
    };
    document.head.appendChild(script);
  });

  inflight.set(name, promise);
  return promise;
}
